import { useEffect } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import { isAfter, isValid as isValidDate, add } from 'date-fns';

import TextField from 'components/CncUI/TextField';
import Dropdown from 'components/CncUI/Dropdown';
import DateTimePicker from 'components/CncUI/DateTimePicker';
import Button from 'components/CncUI/Button';
import Radio from 'components/CncUI/Radio';
import Typography from 'components/CncUI/Typography';
import { Icon, IconList } from 'components/Notifications/IconSelector';
import PRODUCTS from 'components/Notifications/Products';

const products = Object.keys(PRODUCTS).map(k => ({
  value: k,
  label: PRODUCTS[k],
}));

const useStyles = makeStyles(({ spacing }) => ({
  field: {
    // width: 320,
  },
  button: {
    width: 100,
    marginBottom: spacing(3),
  },
  clearButton: {
    width: 'auto',
    marginLeft: spacing(1),
  },
  helperContainer: {
    display: 'flex',
    flexDirection: 'row',
  },
  characterCounter: {
    flexGrow: '1',
    color: 'darkgrey !important',
    textAlign: 'right',
  },
}));

const NotificationBuilder = props => {
  const classes = useStyles();
  const { loading, state, modify } = props;
  const { submitDisabled } = state;
  const {
    register,
    control,
    errors,
    reset,
    watch,
    setValue,
  } = useFormContext();

  const {
    startAt,
    startAtDate,
    expireAt,
    title,
    content,
    primaryLabel,
    secondaryLabel,
  } = watch([
    'startAt',
    'startAtDate',
    'expireAt',
    'title',
    'content',
    'primaryLabel',
    'secondaryLabel',
  ]);

  const userSearchValue = watch('search');
  const groupSelectValue = watch('groupSelect');

  const clearSearch = () => {
    setValue('search', '');
    setValue('groupSelect', '');
    modify({ submitDisabled: true, user: {}, searchError: '' });
  };

  const inputValidationRules = {
    required: 'Missing field',
  };
  const maxLength = length => ({
    maxLength: { value: length, message: `Maximum ${length} characters` },
  });
  const validStartDate = {
    validate: value => isValidDate(value) || 'Date value is not valid',
  };
  const validExpireDate = {
    validate: value => {
      if (!isValidDate(value)) return 'Date value is not valid';
      if (!isAfter(value, startAtDate)) return 'Must be after Start date';
      return true;
    },
  };

  const helperText = (error, maxLength, value) => {
    if (error)
      return (
        <span className={classes.helperContainer}>
          <span>{error}</span>
          <span className={classes.characterCounter}>
            {(value || '').length} of {maxLength} characters
          </span>
        </span>
      );
    else
      return (
        <span className={classes.helperContainer}>
          <span className={classes.characterCounter}>
            {(value || '').length} of {maxLength} characters
          </span>
        </span>
      );
  };

  useEffect(() => {
    if (startAt === 'now') {
      setValue('startAtDate', new Date(), { shouldDirty: true });
    }
  }, [startAt]);

  useEffect(() => {
    if (expireAt === '7d') {
      setValue(
        'expireAtDate',
        startAtDate
          ? add(startAtDate, { days: 7 })
          : add(new Date(), { days: 7 }),
        { shouldDirty: true },
      );
    }
  }, [expireAt]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} container>
        <Typography variant="h5">Notification Content</Typography>
      </Grid>
      <Grid item xs={12} sm={5}>
        <Controller
          render={props => (
            <Dropdown
              id={`NotificationBuilder-${props.name}`}
              label="Type"
              placeholder="Select an option"
              value={props.value}
              renderValue={value => {
                if (value) {
                  const selected = IconList.find(i => i.value === value);
                  return (
                    <span>
                      <Icon type={value} style={{ marginBottom: -4 }} /> &nbsp;{' '}
                      {selected?.label}
                    </span>
                  );
                }
                return 'Select an option';
              }}
              onChange={e => props.onChange(e.target.value)}
              inputRef={props.ref}
              light
              fullWidth
              className={classes.dropdown}
              inputProps={{
                id: `NotificationBuilder-${props.name}-input`,
              }}
              error={Boolean(errors[props.name])}
              helperText={
                (errors[props.name] && errors[props.name].message) || ' '
              }
            >
              {IconList.map(record => (
                <Dropdown.Item key={record.value} value={record.value}>
                  <Icon type={record.value} /> &nbsp; {record.label}
                </Dropdown.Item>
              ))}
            </Dropdown>
          )}
          name="type"
          rules={inputValidationRules}
          control={control}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          light
          fullWidth
          id="NotificationBuilder-title"
          name="title"
          label="Title"
          placeholder="Headline of notification"
          inputRef={register({ ...inputValidationRules, ...maxLength(40) })}
          error={Boolean(errors.title)}
          helperText={helperText(
            errors.title && errors.title.message,
            40,
            title,
          )}
          className={classes.field}
        />
      </Grid>
      <Grid item xs={12} sm={11}>
        <Controller
          render={props => (
            <Dropdown
              id={`NotificationBuilder-${props.name}`}
              label="On Behalf of"
              placeholder="Select an option"
              data={products}
              value={props.value}
              onChange={e => props.onChange(e.target.value)}
              inputRef={props.ref}
              light
              fullWidth
              className={classes.dropdown}
              error={Boolean(errors[props.name])}
              helperText={
                (errors[props.name] && errors[props.name].message) || ' '
              }
              inputProps={{
                id: `NotificationBuilder-${props.name}-input`,
              }}
            />
          )}
          name="origin"
          rules={inputValidationRules}
          control={control}
        />
      </Grid>
      <Grid item xs={12} sm={11}>
        <TextField
          light
          fullWidth
          id="NotificationBuilder-content"
          name="content"
          label="Message"
          inputRef={register({
            ...inputValidationRules,
            ...maxLength(250),
          })}
          error={Boolean(errors.content)}
          helperText={helperText(
            errors.content && errors.content.message,
            250,
            content,
          )}
          className={classes.field}
          multiline
          rows={4}
          rowsMax={4}
          InputProps={{
            inputProps: {
              'data-test-id': 'NotificationBuilder-message-input',
              maxLength: 250,
            },
          }}
        />
      </Grid>
      <Grid item xs={12} sm={11}>
        <Typography variant="h5">Primary Button</Typography>
      </Grid>
      <Grid item xs={12} sm={11}>
        <TextField
          light
          fullWidth
          id="NotificationBuilder-primaryLabel"
          name="primaryLabel"
          label="Label"
          placeholder="CTA (ex. “View”)"
          inputRef={register(maxLength(50))}
          error={Boolean(errors.primaryLabel)}
          helperText={helperText(
            errors.primaryLabel && errors.primaryLabel.message,
            50,
            primaryLabel,
          )}
          className={classes.field}
        />
      </Grid>
      <Grid item xs={12} sm={11}>
        <TextField
          light
          fullWidth
          id="NotificationBuilder-primaryUrl"
          name="primaryUrl"
          label="Link"
          placeholder="URL (ex. “https://my.castandcrew.com”)"
          inputRef={register()}
          error={Boolean(errors.primaryUrl)}
          helperText={(errors.primaryUrl && errors.primaryUrl.message) || ' '}
          className={classes.field}
        />
      </Grid>
      <Grid item xs={12} sm={11}>
        <Typography variant="h5">Secondary Button</Typography>
      </Grid>
      <Grid item xs={12} sm={11}>
        <TextField
          light
          fullWidth
          id="NotificationBuilder-secondaryLabel"
          name="secondaryLabel"
          label="Label"
          placeholder="CTA (ex. “View”)"
          inputRef={register(maxLength(50))}
          error={Boolean(errors.secondaryLabel)}
          helperText={helperText(
            errors.secondaryLabel && errors.secondaryLabel.message,
            50,
            secondaryLabel,
          )}
          className={classes.field}
        />
      </Grid>
      <Grid item xs={12} sm={11}>
        <TextField
          light
          fullWidth
          id="NotificationBuilder-secondaryUrl"
          name="secondaryUrl"
          label="Link"
          placeholder="URL (ex. “https://my.castandcrew.com”)"
          inputRef={register()}
          error={Boolean(errors.secondaryUrl)}
          helperText={
            (errors.secondaryUrl && errors.secondaryUrl.message) || ' '
          }
          className={classes.field}
        />
      </Grid>
      <Grid item xs={12} sm={11} container>
        <Grid item xs={12}>
          <Typography variant="caption" style={{ marginBottom: 0 }}>
            Expires
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Controller
            render={props => (
              <Radio.Group
                id="NotificationExpires-radioGroup"
                value={props.value}
                onChange={e => {
                  props.onChange(e.currentTarget.value);
                }}
                row
                style={{ marginBottom: 16 }}
              >
                <Radio value="7d" id="NotificationExpires-7d">
                  After 7 days from sent
                </Radio>
                <Radio value="onDate" id="NotificationExpires-onDate">
                  On date
                </Radio>
              </Radio.Group>
            )}
            name="expireAt"
            control={control}
          />
        </Grid>
        {expireAt === 'onDate' && (
          <Grid item xs={12}>
            <Controller
              render={props => (
                <DateTimePicker
                  light
                  fullWidth
                  id="NotificationBuilder-expireAtDate"
                  name={props.name}
                  value={props.value}
                  onChange={value => {
                    props.onChange(value);
                  }}
                  onBlur={props.onBlur}
                  error={!!errors.expireAtDate}
                  helperText={
                    errors.expireAtDate && errors.expireAtDate.message
                  }
                />
              )}
              name="expireAtDate"
              control={control}
              rules={{ ...inputValidationRules, ...validExpireDate }}
            />
          </Grid>
        )}
      </Grid>
      <Grid item xs={12} sm={11} container>
        <Grid item xs={12}>
          <Typography variant="caption" style={{ marginBottom: 0 }}>
            Starts
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Controller
            render={props => (
              <Radio.Group
                id="NotificationStart-radioGroup"
                value={props.value}
                onChange={e => {
                  props.onChange(e.currentTarget.value);
                }}
                row
                style={{ marginBottom: 16 }}
              >
                <Radio value="now" id="NotificationStart-now">
                  Immediately
                </Radio>
                <Radio value="onDate" id="NotificationStart-onDate">
                  On date
                </Radio>
              </Radio.Group>
            )}
            name="startAt"
            control={control}
          />
        </Grid>
        {startAt === 'onDate' && (
          <Grid item xs={12}>
            <Controller
              render={props => (
                <DateTimePicker
                  light
                  fullWidth
                  id="NotificationBuilder-startAtDate"
                  name={props.name}
                  value={props.value}
                  onChange={value => {
                    props.onChange(value);
                  }}
                  onBlur={props.onBlur}
                  error={!!errors.startAtDate}
                  helperText={errors.startAtDate && errors.startAtDate.message}
                />
              )}
              name="startAtDate"
              control={control}
              rules={{ ...inputValidationRules, ...validStartDate }}
            />
          </Grid>
        )}
      </Grid>

      <Grid item xs={12} sm={11} container direction="row-reverse">
        <Grid item>
          <Button
            id="NotificationBuilder-submitButton"
            loading={loading}
            disabled={
              (errors && errors.length > 0) ||
              (!userSearchValue && !groupSelectValue) ||
              submitDisabled
            }
            className={classes.button}
            type="submit"
          >
            Submit
          </Button>
        </Grid>
        <Grid item>
          <Button
            id="NotificationBuilder-ClearButton"
            loading={loading}
            disabled={Boolean(errors.search)}
            className={classes.clearButton}
            onClick={() => {
              clearSearch();
              reset();
            }}
            secondary
            style={{ marginRight: 8 }}
          >
            Clear
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default NotificationBuilder;
